import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

const CHECK_EVERY_MS = 10 * 60 * 1000;

/** Avisa cuando hay una version nueva de la app y la aplica al pulsar "Actualizar app". */
@Component({
  selector: 'app-update-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="ready()" class="update-banner" role="alert">
      <span>Hay una nueva versión de SmartBreak disponible.</span>
      <button type="button" (click)="apply()" [disabled]="applying()">
        {{ applying() ? 'Actualizando…' : 'Actualizar app' }}
      </button>
    </div>
  `,
  styles: [
    `
      .update-banner {
        position: fixed;
        left: 12px;
        right: 12px;
        bottom: calc(76px + env(safe-area-inset-bottom));
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 14px;
        border-radius: 16px;
        background: rgba(15, 23, 42, 0.96);
        border: 1px solid rgba(16, 185, 129, 0.6);
        color: #e2e8f0;
        font-size: 0.85rem;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
      }
      button {
        flex-shrink: 0;
        border: 0;
        border-radius: 10px;
        padding: 8px 14px;
        font-weight: 700;
        font-size: 0.8rem;
        color: #fff;
        background: #10b981;
        cursor: pointer;
      }
      button:disabled {
        opacity: 0.6;
      }
    `
  ]
})
export class UpdateBannerComponent implements OnInit, OnDestroy {
  private readonly sw = inject(SwUpdate, { optional: true });
  private subs = new Subscription();
  private timer: ReturnType<typeof setInterval> | null = null;

  readonly ready = signal(false);
  readonly applying = signal(false);

  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') this.check();
  };

  ngOnInit(): void {
    const sw = this.sw;
    if (!sw || !sw.isEnabled) return; // desarrollo: sin service worker no hay versiones que avisar
    this.subs.add(
      sw.versionUpdates
        .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
        .subscribe(() => this.ready.set(true))
    );
    this.check();
    this.timer = setInterval(() => this.check(), CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  private check(): void {
    this.sw?.checkForUpdate().catch(() => {});
  }

  async apply(): Promise<void> {
    this.applying.set(true);
    try {
      await this.sw?.activateUpdate();
    } catch {
      // si falla, igual se recarga y la siguiente carga trae lo nuevo
    }
    document.location.reload();
  }
}
